import { fromBaseRecord } from '../janus-engine/compile/from-base-context';
import type { BaseEnvelope } from '../janus-engine/compile/base-patch';
import type { AppliedNewEntity } from '../janus-engine/transport/applied-frame';
import { encounterCreatureField, encounterField } from '../shared/array-types';
import { _ftCamField } from '../shared/campaign-field';
import { actorMovementPermission } from '../shared/actor-movement-permission';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const field = (row: Row, key: string): unknown => encounterCreatureField(row.encounter_creatures, key);

/** Birth frames are viewer independent. Project only their newborn grants from
 * the installed raw rows, before scene/dock observers see the display change. */
export function projectNewbornControl(envelope: BaseEnvelope, hosts: Iterable<Row>, births: readonly AppliedNewEntity[]): void {
    if (!births.length || envelope.entity === 'inventory') return;
    const user = window.FateTide?.user, uid = upper(user?.id);
    const context = fromBaseRecord(envelope.base.encounter_context) ? envelope.base.encounter_context : envelope.base;
    const parentId = upper(encounterField(context, 'encounter_master_parent_id'));
    const ft = window.FateTide;
    const campaign = [context.campaign_arr, ft?.campaignCache?.get(parentId), ft?.currentCampaignArr]
        .find((value): value is Row => fromBaseRecord(value) && parentId !== '' && upper(_ftCamField(value, 'campaign_id')) === parentId);
    const gm = upper(_ftCamField(campaign, 'campaign_gm'));
    const staff = ['employee', 'admin', 'super_admin'].includes(String(user?.primaryRole ?? ''));
    const admin = ['admin', 'super_admin'].includes(String(user?.primaryRole ?? ''));
    const members = Array.isArray(envelope.base.encounter_creatures_arr) ? envelope.base.encounter_creatures_arr : [];
    const owners = new Map(births.map(birth => [upper(birth.entity_id), birth.base]));
    const direct = (row: Row): boolean => uid !== '' && (staff
        || actorMovementPermission(uid, upper(field(row, 'encounter_creature_created_by_id')), gm, user?.primaryRole,
            upper(encounterField(context, 'encounter_created_by_id')),
            envelope.entity === 'encounter' && fromBaseRecord(envelope.viewer?.permissions) && envelope.viewer.permissions.can_edit)
        || uid === upper(ftLayerValue(row.player_characters, 'permissioned') ?? ftLayerValue(row, 'permissioned')));
    const grants = new Map<string, Row>();
    for (const birth of births) {
        const raw = birth.base;
        let player = direct(raw);
        const controllerId = upper(field(raw, 'controller_encounter_creature_id'));
        if (!player && controllerId) {
            const controller = owners.get(controllerId) ?? members.find((row): row is Row => fromBaseRecord(row)
                && upper(field(row, 'encounter_creature_id')) === controllerId);
            if (controller) { owners.set(controllerId, controller); player = direct(controller); }
        }
        grants.set(upper(birth.entity_id), { player,
            campaign: uid !== '' && (uid === gm || admin), copy: uid !== '' && admin });
    }
    const viewer = envelope.viewer ??= {};
    if (envelope.entity === 'encounter') {
        if (!fromBaseRecord(viewer.members)) viewer.members = {};
        const permissions = viewer.members as Row;
        for (const [id, grant] of grants) permissions[id] = grant;
    } else {
        const permission = grants.get(upper(envelope.entity_id));
        if (permission) envelope.viewer = { ...viewer, permissions: permission };
    }
    for (const host of hosts) {
        const rows = Array.isArray(host.encounter_creatures_arr) ? host.encounter_creatures_arr : [host];
        for (const row of rows) if (fromBaseRecord(row)) {
            const grant = grants.get(upper(field(row, 'encounter_creature_id')));
            if (grant) row.permissions = grant;
        }
    }
}
